import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { lovable } from "@/integrations/lovable/index";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { REPRESENTATION_OPTIONS, type OrgType } from "@/lib/entitlements";
import {
  ClipboardCheck,
  Wrench,
  ShoppingBag,
  GraduationCap,
  Landmark,
  Briefcase,
  ArrowRight,
} from "lucide-react";

/* ── Intent cards ── */
const INTENTS = [
  { key: "assessment", label: "Run Assessment", desc: "Evaluate age-friendly readiness", icon: ClipboardCheck },
  { key: "services", label: "Improve with Services", desc: "Get implementation support", icon: Wrench },
  { key: "solutions", label: "Find Solutions", desc: "Browse verified vendors", icon: ShoppingBag },
  { key: "advisory", label: "Get Expert Advice", desc: "Connect with specialists", icon: GraduationCap },
  { key: "funding", label: "Testbed", desc: "Living labs, AgeTech testing & scaling", icon: Landmark },
  { key: "vendor", label: "I'm a Service Vendor", desc: "List my UK-verified services", icon: Briefcase },
] as const;

/* ── Main component ── */
interface OnboardingWizardProps {
  onBackToSignIn: () => void;
  onClose?: () => void;
  onIntentStepShown?: () => void;
}

export default function OnboardingWizard({ onBackToSignIn, onClose, onIntentStepShown }: OnboardingWizardProps) {
  const [step, setStep] = useState(0); // 0=signup, 1=intent
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [representation, setRepresentation] = useState<OrgType>("independent");
  const [organisationName, setOrganisationName] = useState("");
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const { signUp } = useAuth();
  const { toast } = useToast();

  const needsOrgName = representation !== "independent";

  const handleSubmit = async () => {
    if (password.length < 6) {
      toast({ title: "Password too short", description: "Must be at least 6 characters.", variant: "destructive" });
      return;
    }
    if (needsOrgName && !organisationName.trim()) {
      toast({
        title: "Organisation required",
        description: "Tell us which organisation you represent.",
        variant: "destructive",
      });
      return;
    }
    setSubmitting(true);
    const metadata: Record<string, string> = {
      full_name: fullName || "",
      representation,
      ...(needsOrgName ? { organisation: organisationName.trim() } : {}),
    };
    const { error } = await signUp(email, password, metadata);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
      setSubmitting(false);
      return;
    }
    toast({ title: "Check your email", description: "A confirmation link has been sent to your email address." });
    setSubmitting(false);
    setStep(1);
    onIntentStepShown?.();
  };


  const handleIntentSelect = (key: string) => {
    localStorage.setItem("safe_onboarding_intent", key);
    localStorage.setItem("safe_user_primary_intent", key);
    onClose?.();
  };

  if (step === 1) {
    return (
      <div className="w-full animate-fade-in">
        <h2 className="text-xl font-bold text-foreground">What do you want to do first?</h2>
        <p className="mt-1 text-sm text-muted-foreground mb-5">
          Choose your starting point. You can explore everything later.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {INTENTS.map((intent) => (
            <button
              key={intent.key}
              type="button"
              onClick={() => handleIntentSelect(intent.key)}
              className="group relative text-left px-4 py-3 border border-border bg-card hover:border-primary hover:bg-accent/40 transition-colors duration-150"
            >
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0 flex items-center justify-center h-9 w-9 border border-border bg-muted text-muted-foreground group-hover:text-primary group-hover:border-primary/40 group-hover:bg-primary/5 transition-colors">
                  <intent.icon className="h-4 w-4" strokeWidth={1.75} />
                </div>
                <div className="flex-1 min-w-0">
                  <span className="block text-[13px] font-semibold text-foreground leading-tight">{intent.label}</span>
                  <span className="block text-[11px] text-muted-foreground mt-0.5 leading-snug">{intent.desc}</span>
                </div>
                <ArrowRight className="h-3.5 w-3.5 text-muted-foreground/40 group-hover:text-primary group-hover:translate-x-0.5 transition-all" />
              </div>
            </button>
          ))}
        </div>

        <button
          type="button"
          className="mt-4 w-full text-center text-xs text-muted-foreground hover:text-foreground transition-colors"
          onClick={() => onClose?.()}
        >
          Skip for now
        </button>
      </div>
    );
  }

  return (
    <div className="w-full">
      <h2 className="text-xl font-bold text-foreground">Create Your Account</h2>
      <p className="mt-1 text-sm text-muted-foreground mb-5">
        Assess, improve, and unlock funding for age-friendly environments
      </p>

      <div className="space-y-3">
        <div className="space-y-1">
          <Label htmlFor="onb-fullname" className="text-sm font-semibold text-foreground">Full Name</Label>
          <Input id="onb-fullname" placeholder="Jane Smith" value={fullName} onChange={(e) => setFullName(e.target.value)} className="h-10 text-base border-2 border-input" />
        </div>
        <div className="space-y-1">
          <Label htmlFor="onb-email" className="text-sm font-semibold text-foreground">Work Email</Label>
          <Input id="onb-email" type="email" placeholder="name@example.gov" value={email} onChange={(e) => setEmail(e.target.value)} required className="h-10 text-base border-2 border-input" />
        </div>
        <div className="space-y-1">
          <Label htmlFor="onb-password" className="text-sm font-semibold text-foreground">Password</Label>
          <Input id="onb-password" type="password" placeholder="Min. 6 characters" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} className="h-10 text-base border-2 border-input" />
        </div>

        <div className="space-y-1">
          <Label className="text-sm font-semibold text-foreground">What are you representing?</Label>
          <Select value={representation} onValueChange={(v) => setRepresentation(v as OrgType)}>
            <SelectTrigger className="h-10 text-base border-2 border-input">
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent>
              {REPRESENTATION_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-[11px] text-muted-foreground">
            {needsOrgName
              ? "Your assessments will belong to this organisation."
              : "We'll create a personal assessment context just for you."}
          </p>
        </div>

        {needsOrgName && (
          <div className="space-y-1">
            <Label htmlFor="onb-org" className="text-sm font-semibold text-foreground">
              Organisation name
            </Label>
            <Input
              id="onb-org"
              placeholder="e.g. Manchester City Council"
              value={organisationName}
              onChange={(e) => setOrganisationName(e.target.value)}
              className="h-10 text-base border-2 border-input"
            />
          </div>
        )}


        <div className="flex items-start gap-2.5 pt-1">
          <Checkbox id="onb-agree" checked={agreedToTerms} onCheckedChange={(v) => setAgreedToTerms(v === true)} className="mt-0.5" />
          <Label htmlFor="onb-agree" className="text-xs text-muted-foreground leading-relaxed font-normal cursor-pointer">
            I agree to the{" "}
            <Link to="/terms" className="text-primary underline underline-offset-2" target="_blank">Terms</Link>,{" "}
            <Link to="/privacy" className="text-primary underline underline-offset-2" target="_blank">Privacy</Link>,{" "}
            <Link to="/cookies" className="text-primary underline underline-offset-2" target="_blank">Cookies</Link>, and{" "}
            <Link to="/acceptable-use" className="text-primary underline underline-offset-2" target="_blank">Acceptable Use</Link>.
          </Label>
        </div>

        <Button
          type="button"
          className="w-full h-10 text-sm font-semibold tracking-wide"
          disabled={submitting || !agreedToTerms || !email || !password}
          onClick={handleSubmit}
        >
          {submitting ? "Please wait…" : "Continue"}
        </Button>

        <div className="flex items-center gap-3 my-1">
          <Separator className="flex-1" />
          <span className="text-[10px] text-muted-foreground uppercase tracking-wide">or</span>
          <Separator className="flex-1" />
        </div>

        <div className="space-y-1.5">
          <Button type="button" variant="outline" className="w-full h-10 text-sm font-medium" disabled={submitting} onClick={async () => {
            setSubmitting(true);
            try {
              const { error } = await lovable.auth.signInWithOAuth("google", { redirect_uri: window.location.origin });
              if (error) toast({ title: "Error", description: error.message, variant: "destructive" });
            } catch { toast({ title: "Sign-in failed", description: "Please try email/password instead.", variant: "destructive" }); }
            setTimeout(() => setSubmitting(false), 4000);
          }}>
            <svg className="mr-2 h-3.5 w-3.5" viewBox="0 0 24 24">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            Continue with Google
          </Button>
        </div>
      </div>

      <p className="mt-4 text-xs text-muted-foreground">
        Already have an account?{" "}
        <button type="button" className="text-primary font-semibold underline underline-offset-4" onClick={onBackToSignIn}>
          Sign in
        </button>
      </p>
    </div>
  );
}
